import React from "react";

export type AnswerType = "text" | "date" | "textarea" | "radio" | "checkbox" | "select";

export interface Question {
  _id: string;
  question: string;
  answerType: AnswerType;
  options?: string[];
  required: boolean;
  disabled?: boolean;
  placeholder?: string;
}

interface QuestionInputProps {
  question: Question;
  value?: string | string[];
  onChange: (value: string | string[]) => void;
  disabled?: boolean;
  required?: boolean;
  // Brand colour of the environment the questionnaire belongs to.
  accent?: string;
}

const DEFAULT_ACCENT = "#0058c3";

const baseInputClasses =
  "w-full px-4 py-3 text-[14px] text-slate-800 bg-white border border-slate-200 rounded-xl outline-none transition-all " +
  "placeholder:text-slate-400 hover:border-slate-300 focus:border-[color:var(--q-accent)] " +
  "focus:ring-4 focus:ring-[color:var(--q-accent)]/12 disabled:bg-slate-50 disabled:cursor-not-allowed";

const QuestionInput: React.FC<QuestionInputProps> = ({ question, value, onChange, disabled, required, accent }) => {
  const valString = typeof value === "string" ? value : "";
  const valArray = Array.isArray(value) ? value : [];
  // The accent is passed down as a CSS variable so focus/selection states match
  // the client's brand without any per-brand code.
  const style = { ["--q-accent" as any]: accent || DEFAULT_ACCENT } as React.CSSProperties;

  switch (question.answerType) {
    case "text":
      return (
        <input
          type="text"
          value={valString}
          placeholder={question.placeholder || ""}
          onChange={(e) => onChange(e.target.value)}
          className={baseInputClasses}
          style={style}
          disabled={disabled}
          required={required}
        />
      );

    case "date":
      return (
        <input
          type="date"
          value={valString}
          placeholder={question.placeholder || ""}
          onChange={(e) => onChange(e.target.value)}
          className={baseInputClasses}
          style={style}
          disabled={disabled}
          required={required}
        />
      );

    case "textarea":
      return (
        <textarea
          value={valString}
          placeholder={question.placeholder || ""}
          onChange={(e) => onChange(e.target.value)}
          className={`${baseInputClasses} h-28 resize-y leading-relaxed`}
          style={style}
          disabled={disabled}
          required={required}
        />
      );

    // Options render as selectable cards — far easier to tap on mobile than a
    // bare radio/checkbox, and the selected state shows the brand colour.
    case "radio":
    case "checkbox": {
      const isChecked = (opt: string) =>
        question.answerType === "radio" ? valString === opt : valArray.includes(opt);
      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" style={style}>
          {question.options?.map((opt, i) => {
            const checked = isChecked(opt);
            return (
              <label
                key={i}
                className={`flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl border text-[14px] cursor-pointer transition-all ${
                  checked
                    ? "border-[color:var(--q-accent)] bg-[color:var(--q-accent)]/[0.06] text-slate-900 font-medium"
                    : "border-slate-200 text-slate-700 hover:border-slate-300 hover:bg-slate-50"
                } ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <input
                  type={question.answerType}
                  value={opt}
                  checked={checked}
                  disabled={disabled}
                  required={required && question.answerType === "radio"}
                  onChange={(e) => {
                    if (question.answerType === "radio") {
                      onChange(opt);
                    } else {
                      const newValue = [...valArray];
                      if (e.target.checked) newValue.push(opt);
                      else newValue.splice(newValue.indexOf(opt), 1);
                      onChange(newValue);
                    }
                  }}
                  className="h-4 w-4 shrink-0"
                  style={{ accentColor: accent || DEFAULT_ACCENT }}
                />
                <span className="leading-snug">{opt}</span>
              </label>
            );
          })}
        </div>
      );
    }

    case "select":
      return (
        <select
          value={valString}
          onChange={(e) => onChange(e.target.value)}
          className={`${baseInputClasses} appearance-none bg-[length:16px] bg-[right_14px_center] bg-no-repeat pr-10`}
          style={{
            ...style,
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2.5' stroke-linecap='round'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E\")",
          }}
          disabled={disabled}
          required={required}
        >
          <option value="">{question.placeholder || "Select an option"}</option>
          {question.options?.map((opt, i) => (
            <option key={i} value={opt}>{opt}</option>
          ))}
        </select>
      );

    default:
      return null;
  }
};

export default QuestionInput;
